import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { borderRadius, colors, spacing, strokeWidth, typography } = themeVariables;

/**
 * The card's elevation and the dimming over an out-of-stock image.
 *
 * The theme has no shadow token and only a full 70% scrim, so both are mixed
 * here from the default content colour: the colour stays a theme decision and
 * rethemes with it, leaving only the geometry and the design's own percentages
 * stated locally. Add `shadow.base` and a lighter `overlay` token if a second
 * component needs either.
 *
 * The design specifies #0E0E11 where this uses the brand black, so both come out
 * a shade lighter and warmer. Imperceptible at 10%; well within tolerance at 30%.
 */
export const ELEVATION = `0 0 4px 0 color-mix(in srgb, var(${colors.content.default}) 10%, transparent)`;
const DIM = `color-mix(in srgb, var(${colors.content.default}) 30%, transparent)`;

/**
 * The height the delivery block is held to: two lines of body `sm` plus the gap
 * between them, which is exactly what an estimated delivery occupies.
 *
 * Reserved rather than left to content so that a card showing "Out of stock" on
 * one line, or a wrapped special-event label, still lines its delivery row up
 * with its neighbours in a grid. A minimum rather than a fixed height, so
 * unexpected content grows the card instead of being clipped.
 */
export const DELIVERY_MIN_HEIGHT = `calc(var(${typography.body.sizes.sm.lineHeight}) * 2 + var(${spacing.xxs}) / 2)`;

/* The card fills the width and the height it is given. No breakpoints here on
   purpose: it responds to its container, not to the viewport. The design's 308px
   is a reference width, not a constraint.

   `height: 100%` is what lets a row of cards in a grid line up: the card takes
   the full row height, the image and the delivery block keep fixed heights at
   either end, and the details between them absorb the difference. Against a
   parent of auto height it resolves to auto, so it is safe outside a grid too.

   `position: relative` is the anchor for the title link's overlay, which is what
   makes the whole card clickable through a single link.

   The focus ring is drawn here rather than on that overlay, because the overlay sits
   below the image area and a ring drawn at its bounds would have its top half hidden
   behind the image. It has to be a pseudo-element with a z-index rather than an
   `outline` on the card for the same reason: an outline paints with the card's own
   background, which every positioned descendant covers.

   A border on an inset box rather than an outline, so it follows the card's rounded
   corners; `pointer-events: none` so it stays out of the way of the swipe. */
export const card = css`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: var(${borderRadius.md.mobile});
  background-color: var(${colors.surface.default});
  box-shadow: ${ELEVATION};

  &:has(a:focus-visible)::after {
    position: absolute;
    inset: 0;
    z-index: 3;
    border: 2px solid var(${colors.stroke.focus});
    border-radius: inherit;
    content: '';
    pointer-events: none;
  }
`;

/**
 * The image area: its own stacking context, holding the images and everything
 * that sits over them.
 *
 * `z-index` is what makes the swipe work. The title link's overlay covers the whole
 * card, and a scroller underneath it never receives the touch, because the overlay
 * is the scroller's sibling rather than its ancestor and so offers nothing to
 * scroll. Lifting the whole area above the overlay fixes that in one place, and
 * keeps the ordering inside it local and small: images, then the hover image, then
 * the out-of-stock dimming, then the badge and dots, then the favourite button. Only
 * the card's focus ring goes above the whole area.
 *
 * Never shrinks: the image's height comes from the card's width via its aspect
 * ratio, which is what makes it the same on every card in a row.
 */
export const media = css`
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  line-height: 0;
`;

/* Dims the image so an out-of-stock product reads as unavailable at a glance.
   Decorative: the wording below the divider carries the meaning. The strength is
   mixed into the colour rather than layered on with `opacity`, so two alphas are
   not compounded.

   `pointer-events: none` because it now covers the images: without it, the one card
   in a listing that is out of stock would be the one whose image could neither be
   tapped nor swiped. */
export const outOfStockOverlay = css`
  position: absolute;
  inset: 0;
  z-index: 2;
  background-color: ${DIM};
  pointer-events: none;
`;

/* The card owns where the badge sits, so the badge itself stays free of layout.
   Capped in width so a long label cannot run under the favourite button, which
   the design insets further from the edge than the badge.

   `pointer-events: none` is what keeps a tap on the badge opening the product. The
   badge is not interactive, so it used to achieve that by sitting below the title
   link's overlay; now that the image area sits above that overlay, the badge has to
   be raised above the images and says it directly instead. The tap falls through to
   the slide's own link. */
export const badgeSlot = css`
  position: absolute;
  top: var(${spacing.xs});
  left: var(${spacing.xs});
  z-index: 3;
  display: flex;
  max-width: calc(100% - var(${spacing.xs}) - var(${spacing.sm}));
  pointer-events: none;
`;

/* Above everything else over the image, so favouriting does not navigate. */
export const favouriteSlot = css`
  position: absolute;
  right: var(${spacing.sm});
  bottom: var(${spacing.sm});
  z-index: 4;
  display: flex;
`;

/* Takes whatever height is left once the image has had its share, so the card
   below the image is what stretches in a grid rather than the image.

   One `sm` value does all of the card's spacing below the image: as padding it
   sets the gap from the image to the partner name, the inset either side, and the
   card's bottom edge; as the gap between this column's three children it sets the
   space above and below the divider. The divider therefore also stops `sm` short
   of each edge, which is how the design draws it. */
export const body = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(${spacing.sm});
  min-height: 0;
  padding: var(${spacing.sm});
`;

/* Everything between the image and the delivery block. This is the part that
   flexes, so a one-line title and a two-line title still leave their cards'
   delivery rows level with each other. Content stays at the top; the slack
   collects underneath it. */
export const details = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(${spacing.xxs});
`;

/* Held to a consistent height and centred within it, so the delivery row sits on
   the same line across a row of cards whether it shows a date, a wrapped event
   label, or "Out of stock". */
export const deliverySlot = css`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  justify-content: center;
  min-height: ${DELIVERY_MIN_HEIGHT};
`;

export const partner = css`
  margin: 0;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.link});
`;

/* The heading typeface, with its weight and tighter letter-spacing, on a body
   size. The card title is a heading and should read as one, but the heading type
   scale starts at 18px where the design's title is 15px, so the size comes from
   the body scale. Mixing the two groups is deliberate; the alternative is a
   title a size larger than the design and a taller card. Add a heading `xxs`
   step and this can take the whole group. */
export const title = css`
  display: -webkit-box;
  overflow: hidden;
  margin: 0;
  font-family: var(${typography.heading.fontFamily});
  font-weight: var(${typography.heading.fontWeight});
  font-size: var(${typography.heading.sizes.xs.fontSize});
  line-height: var(${typography.heading.sizes.xs.lineHeight});
  letter-spacing: var(${typography.heading.letterSpacing});
  color: var(${colors.content.default});
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

/**
 * One link per card, as before, but the card now has content above and below the
 * title that cannot sit inside an anchor: the badge, the favourite button and the
 * partner name. So the link stretches over the whole card with a pseudo-element
 * instead of wrapping it. Screen readers get a single link named by the title.
 *
 * The image area sits above this overlay so that it can be swiped, so each slide
 * carries its own tap target and the image is still tappable. The focus ring is
 * drawn on the card for the same reason.
 */
export const titleLink = css`
  color: inherit;
  text-decoration: none;

  &::after {
    position: absolute;
    inset: 0;
    z-index: 1;
    content: '';
  }
`;

/* The promotion needs a code typing in, so it earns the emphasis of its own
   tinted row. */
export const promotion = css`
  margin: 0;
  padding: var(${spacing.xxs}) var(${spacing.xs});
  border-radius: var(${borderRadius.md.mobile});
  background-color: var(${colors.surface.brand});
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  font-weight: 700;
  color: var(${colors.content.brand});
  text-align: center;
`;

/* No margin of its own: the body's gap sets the space either side of it. */
export const divider = css`
  flex-shrink: 0;
  margin: 0;
  border: 0;
  border-top: var(${strokeWidth}) solid var(${colors.stroke.default});
`;

/* Centred and in caps, replacing the delivery block: there is nothing to say
   about delivery for something the customer cannot buy. */
export const outOfStock = css`
  margin: 0;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.secondary});
  text-align: center;
  text-transform: uppercase;
`;

/* Full bleed to the card's edges, so it reads as a label on the card rather than
   as another line of product copy. Sits below the delivery block, so a sponsored
   card's delivery row is one band higher than an unsponsored neighbour's. That
   follows from where the design puts it, and reserving an empty band on every
   other card to compensate would be worse. */
export const sponsored = css`
  flex-shrink: 0;
  margin: 0;
  padding: var(${spacing.xs});
  background-color: var(${colors.surface.brand});
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.secondary});
  text-align: center;
`;
